/* import __COLOCATED_TEMPLATE__ from './conversation-tags.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type Conversation from 'embercom/objects/inbox/conversation';
import type SidebarSection from 'embercom/objects/inbox/sidebar-section';
import Tag, { type TagActionType, type TagMenuSource } from 'embercom/objects/inbox/tag';
import type IntlService from 'embercom/services/intl';
import type InboxApi from 'embercom/services/inbox-api';
import type Session from 'embercom/services/session';
import type RouterService from '@ember/routing/router-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { tracked } from '@glimmer/tracking';
import { trackedReset } from 'tracked-toolbox';
import { includesTag } from 'embercom/objects/inbox/taggable-part';
import type CommandKService from 'embercom/services/command-k';
import { CommandKTagActionId } from 'embercom/objects/inbox/command-k/tag-item';
import { type SidebarSectionComponentArgs } from 'embercom/lib/inbox2/sidebar-types';

type Args = SidebarSectionComponentArgs<{
  conversation: Conversation;
  sidebarSection: SidebarSection;
  allTags: Record<string, Tag>;
  loadAllTags: Function;
  commandKActionPriority?: number;
}>;

interface Signature {
  Args: Args;
}

type SectionId = 'conversation-tags';

export default class ConversationTags extends Component<Signature> {
  readonly sectionId: SectionId = 'conversation-tags';

  @service declare inboxApi: InboxApi;
  @service declare intl: IntlService;
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare notificationsService: any;
  @service declare commandK: CommandKService;

  @trackedReset('args.conversation.id') localTags: Tag[] | undefined = undefined;
  @tracked tagMenuSource: TagMenuSource = null;
  @tracked tagActionType: TagActionType = null;

  get conversationTags(): Tag[] {
    // Use local tags if the user has modified them, otherwise read from conversation
    if (this.localTags !== undefined) {
      return this.localTags;
    }
    return this.args.conversation?.taggings?.allTags ?? [];
  }

  get limitedConversationTags(): Tag[] {
    return this.conversationTags.slice(0, 5);
  }

  get context() {
    return {
      conversation: this.args.conversation,
      tags: this.conversationTags,
    };
  }

  @action setTagActionType(type: TagActionType) {
    this.tagActionType = type;
  }

  @action async addTag() {
    if (!this.args.conversation) {
      return;
    }

    this.commandK.findAndShow(CommandKTagActionId.TagConversation, () => {});
  }

  @action async removeTag(tag: Tag) {
    this.setTagActionType('removed');
    await this.updateTagsForConversation({
      conversationId: this.args.conversation?.id,
      updatedTags: this.conversationTags.filter((x) => x !== tag),
    });
  }

  private async updateTagsForConversation(props: {
    conversationId: number | undefined;
    updatedTags: Tag[];
  }) {
    if (!props.conversationId) {
      return;
    }

    let oldTags = this.conversationTags;

    try {
      this.localTags = props.updatedTags;
      let response = await this.inboxApi.updateTagsForConversation(
        props.conversationId,
        props.updatedTags,
      );
      if (response?.tags?.length && props.updatedTags.some((tag) => !tag.id)) {
        let updatedTags = props.updatedTags.map((tag) => {
          if (!tag.id) {
            let createdTag = response.tags.find((t: Tag) => t.name === tag.name);
            if (createdTag) {
              return new Tag(
                createdTag.id,
                createdTag.name,
                this.session.teammate,
                new Date(createdTag.created_at),
              );
            }
          }
          return tag;
        });
        this.localTags = updatedTags;
      }
    } catch (error: unknown) {
      this.notificationsService.notifyResponseError(error);
      this.localTags = oldTags;
    }
  }

  @action async tagConversation(
    tag: Tag,
    context: { conversation: Conversation; tags: Tag[] },
  ): Promise<void> {
    let conversationIsTagged = includesTag(context.tags, tag);
    let updatedTags: Tag[] = [];

    if (conversationIsTagged) {
      updatedTags = context.tags.filter(({ id }) => id !== tag.id);
    } else {
      updatedTags = [...context.tags, new Tag(tag.id, tag.name, this.session.teammate)];
    }
    this.setTagActionType(conversationIsTagged ? 'removed' : 'added');
    await this.updateTagsForConversation({
      conversationId: context.conversation?.id,
      updatedTags,
    });
  }

  @action manageTags() {
    let url: string = this.router.urlFor('apps.app.settings.data.tags', this.session.workspace);
    safeWindowOpen(url);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::ConversationTags': typeof ConversationTags;
    'inbox2/conversation-details-sidebar/sidebar-sections/conversation-tags': typeof ConversationTags;
  }
}
